"use client";

import React, { useEffect, useState } from "react";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import { Trash2 } from "lucide-react";
import { Link } from "@/i18n/routing";
import { Button } from "@/components/ui/button";
import { RESOURCE_FAMILY_ICONS } from "@/components/resource/search/family-meta";
import type { ResourceSearchFamily } from "@/db/definitions";
import {
  isUsableRecentResource,
  RecentResourceKind,
  useRecentResourceStore,
} from "@/lib/store/recent-resource-store";

const kindFamilies: Record<RecentResourceKind, ResourceSearchFamily> = {
  ARTICLE: "ARTICLE",
  GLOSSARY_TERM: "GLOSSARY",
  HANDBOOK_CHAPTER: "HANDBOOK",
  PLATFORM: "PLATFORM",
  INSURANCE_PROVIDER: "INSURANCE",
};

/** The latest resources opened in this browser, newest first. */
const RecentResourcesList: React.FC = () => {
  const t = useTranslations("resource.recent");
  const tKinds = useTranslations("resource-search.view.kinds");
  const locale = useLocale();
  const format = useFormatter();
  const { items, clear } = useRecentResourceStore();
  // The store reads storage on the client only; render after mount so the
  // shared server HTML never depends on it.
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const visible = isMounted
    ? items.filter(isUsableRecentResource).slice(0, 6)
    : [];

  if (!visible.length) {
    return null;
  }

  const now = Date.now();

  return (
    <section
      aria-labelledby="recent-resources-title"
      className="google-anno-skip flex w-full flex-col gap-6 sm:gap-8"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("eyebrow")}
          </p>
          <h2
            id="recent-resources-title"
            className="text-2xl font-semibold tracking-tight sm:text-3xl"
          >
            {t("title")}
          </h2>
          <p className="max-w-2xl text-sm text-muted-foreground sm:text-base">
            {t("description")}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={clear}
          className="w-fit flex-none"
        >
          <Trash2 className="size-4" />
          {t("clear")}
        </Button>
      </div>

      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
        {visible.map((item, index) => {
          const Icon = RESOURCE_FAMILY_ICONS[kindFamilies[item.kind]];
          const kind = tKinds(item.kind);

          return (
            <li
              key={`${item.kind}-${item.id}`}
              className={index >= 4 ? "hidden sm:block" : undefined}
            >
              <Link
                href={item.path}
                // Slugs differ per language, so other-language reads open
                // in the language they were read in.
                locale={
                  item.locale !== locale ? (item.locale as never) : undefined
                }
                title={t("item-title", { kind, title: item.title })}
                className="group flex h-full items-start gap-3 rounded-2xl border border-border bg-background p-3 shadow-sm transition-all hover:border-zinc-300 hover:shadow-md sm:p-4"
              >
                <span className="flex size-9 flex-none items-center justify-center rounded-xl bg-secondary sm:size-10">
                  <Icon className="size-4 sm:size-5" />
                </span>
                <span className="flex min-w-0 flex-col gap-0.5 sm:gap-1">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    {kind}
                    {item.locale !== locale && (
                      <span
                        title={t("other-language")}
                        className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none text-foreground"
                      >
                        {item.locale}
                      </span>
                    )}
                  </span>
                  <span className="line-clamp-2 text-sm font-semibold sm:text-base">
                    {item.title}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t("viewed", { time: format.relativeTime(item.at, now) })}
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default RecentResourcesList;
